import type { KeyboardEvent } from "react";
import type { Category } from "../api/types";

type Ellipse = [number, number, number, number];
type SketchDef = { body: string; stitch: string; pins: Record<string, [number, number]>; area: Record<string, Ellipse[]> };

// Garment outlines from the demo, plus a skirt drawn in the same style (viewBox 200x240).
const SKETCH: Record<Category, SketchDef> = {
  pants: {
    body: "M52 18 H148 L160 228 H114 L100 100 L86 228 H40 Z",
    stitch: "M52 32 H148 M100 32 V80 M45 216 H85 M115 216 H155",
    pins: { waist: [124, 25], hip: [150, 62], rise: [100, 90], thigh: [67, 124], length: [62, 222] },
    area: { waist: [[100, 25, 50, 10]], hip: [[100, 60, 54, 16]], rise: [[100, 92, 18, 15]], thigh: [[68, 128, 22, 24], [132, 128, 22, 24]], length: [[62, 222, 26, 9], [138, 222, 26, 9]] },
  },
  top: {
    body: "M72 26 Q100 44 128 26 L168 42 L190 92 L162 104 L156 86 L156 214 H44 L44 86 L38 104 L10 92 L32 42 Z",
    stitch: "M48 204 H152 M160 94 L184 84 M40 94 L16 84",
    pins: { shoulder: [150, 40], chest: [100, 112], neckline: [100, 46], tlength: [100, 210], sleeve: [174, 82] },
    area: { shoulder: [[48, 38, 18, 10], [152, 38, 18, 10]], chest: [[100, 112, 50, 30]], neckline: [[100, 38, 30, 14]], tlength: [[100, 210, 58, 9]], sleeve: [[176, 80, 18, 18], [24, 80, 18, 18]] },
  },
  dress: {
    body: "M78 20 L122 20 L136 70 L124 98 L164 228 H36 L76 98 L64 70 Z",
    stitch: "M76 98 H124 M42 218 H158",
    pins: { bust: [100, 60], dwaist: [100, 98], dhip: [100, 142], dlength: [100, 222] },
    area: { bust: [[100, 62, 32, 13]], dwaist: [[100, 98, 26, 7]], dhip: [[100, 142, 42, 17]], dlength: [[100, 222, 64, 9]] },
  },
  skirt: {
    // waistband, then an A-line that flares to the hem
    body: "M62 30 H138 L141 50 L172 214 H28 L59 50 Z",
    stitch: "M60 44 H140 M120 50 V96 M34 202 H166",
    pins: { swaist: [100, 38], ship: [150, 92], slength: [100, 208] },
    area: { swaist: [[100, 40, 42, 10]], ship: [[100, 92, 52, 18]], slength: [[100, 208, 72, 9]] },
  },
};

export type SketchPin = { zone: string; cls: string; label?: string; name: string };

export function Sketch(props: {
  cat: Category;
  pins: SketchPin[];
  vneck?: boolean;
  active?: string | null;
  animate?: boolean;
  onPick?: (zone: string) => void;
  onHover?: (zone: string) => void;
  label?: string;
}) {
  const { cat, pins, vneck, active, animate, onPick, onHover } = props;
  const k = SKETCH[cat] ?? SKETCH.pants;
  const key = (e: KeyboardEvent, z: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onPick?.(z);
    }
  };
  return (
    <svg
      className={"sketch" + (animate ? " fx-pins" : "")}
      viewBox="0 0 200 240"
      role="group"
      aria-label={props.label ?? "Garment sketch. Hover or tap a numbered spot."}
    >
      <path className="body" d={k.body} />
      {pins.flatMap((p) =>
        (k.area[p.zone] ?? []).map((a, i) => (
          <ellipse key={p.zone + i} className={"zarea" + (active === p.zone ? " show" : "")} cx={a[0]} cy={a[1]} rx={a[2]} ry={a[3]} />
        )),
      )}
      <path className="stitch" d={k.stitch} />
      {cat === "top" && <path className="stitch" d={vneck ? "M72 26 L100 64 L128 26" : "M78 31 Q100 50 122 31"} />}
      {pins.map((p, i) => {
        let xy = k.pins[p.zone];
        if (!xy) return null;
        if (cat === "top" && p.zone === "neckline" && vneck) xy = [100, 58];
        return (
          <g
            key={p.zone}
            className={"pin " + p.cls + (active === p.zone ? " on" : "")}
            tabIndex={0}
            role="button"
            aria-label={p.name}
            aria-pressed={active === p.zone}
            style={{ animationDelay: `${120 + i * 110}ms` }}
            onClick={() => onPick?.(p.zone)}
            onMouseEnter={() => onHover?.(p.zone)}
            onFocus={() => onHover?.(p.zone)}
            onKeyDown={(e) => key(e, p.zone)}
          >
            <circle className="hit" cx={xy[0]} cy={xy[1]} r="24" />
            <circle className="halo" cx={xy[0]} cy={xy[1]} r="19" />
            <circle className="dot" cx={xy[0]} cy={xy[1]} r="10" />
            <text x={xy[0]} y={xy[1] + 0.5}>
              {p.label ?? i + 1}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
